import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { GitPullRequest, Terminal, Shield, Cpu, Database, Layout } from 'lucide-react';

export const Experience = () => {
  const { t } = useTranslation();
  const experiences = t('experience.items', { returnObjects: true }) as any[];

  return (
    <section className="py-32 bg-slate-950 relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center text-center mb-24">
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-none mb-6">
            {t('experience.title').split(' ')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">{t('experience.title').split(' ')[1]}</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('experience.subtitle')}
          </p>
        </div>

        <div className="relative">
          {/* Central Timeline Line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-slate-700 to-transparent md:-translate-x-1/2" />

          <div className="space-y-12 md:space-y-24">
            {experiences.map((exp: any, index: number) => {
              const isEven = index % 2 === 0;
              // Icon logic based on keywords in description/role
              let Icon = Terminal;
              if (exp.role.toLowerCase().includes('full stack')) Icon = Layout;
              if (exp.role.toLowerCase().includes('backend')) Icon = Database;
              if (exp.role.toLowerCase().includes('security')) Icon = Shield;
              if (exp.role.toLowerCase().includes('ai') || exp.role.toLowerCase().includes('machine')) Icon = Cpu;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex flex-col md:flex-row gap-8 md:gap-0 ${isEven ? 'md:flex-row-reverse' : ''}`}
                >
                  {/* Node Point */}
                  <div className="absolute left-4 md:left-1/2 top-0 w-3 h-3 rounded-full bg-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.8)] md:-translate-x-1/2 z-20 translate-x-[-5px] mt-6 ring-4 ring-slate-950" />

                  {/* Content Side */}
                  <div className={`md:w-1/2 pl-12 md:pl-0 ${isEven ? 'md:pr-16' : 'md:pl-16'}`}>
                     <div className="group relative bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-purple-500/50 transition-all duration-500 hover:shadow-[0_0_30px_rgba(168,85,247,0.1)]">
                        {/* Decorative Corner */}
                        <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:opacity-100 transition-opacity duration-500">
                           <Icon className="text-purple-500" size={40} />
                        </div>

                        <div className="flex flex-col mb-4">
                           <span className="text-xs font-mono text-purple-400 mb-1">v{3.0 - (index * 0.5)} // {exp.period}</span>
                           <h3 className="text-xl font-bold text-white">{exp.company}</h3>
                           <span className="text-sm font-medium text-slate-500">{exp.role}</span>
                        </div>
                        
                        <div className="mb-6">
                           <div className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                              {t('experience.impact')}
                           </div>
                           <p className="text-slate-400 text-sm leading-relaxed">
                             {exp.description}
                           </p>
                        </div>

                        <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800/50">
                           <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-3">{t('experience.modules')}</div>
                           <div className="flex flex-wrap gap-2">
                              {exp.achievements.map((ach: string, achIndex: number) => (
                                <span key={achIndex} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/20 text-xs text-purple-300 font-mono">
                                   <GitPullRequest size={10} />
                                   {ach}
                                </span>
                              ))}
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Empty Side for Balance */}
                  <div className="md:w-1/2" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
